import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CheckCircle, XCircle, Clock, CalendarIcon, Save, Users, Copy, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useLiveMode } from '../context/LiveModeContext';
import { StudentAvatar } from '../components/StudentAvatar';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

export const AttendancePage = () => {
  const { classId } = useParams();
  const { session, refreshStats } = useLiveMode();
  const [students, setStudents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [lateTimes, setLateTimes] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  const fetchData = async () => {
    setLoading(true);
    try {
      const sessionParam = session?.session_id ? `&session_id=${session.session_id}` : '';
      const [studentsRes, classesRes, attendanceRes] = await Promise.all([
        fetch(`${API}/students/${classId}`, { credentials: 'include' }),
        fetch(`${API}/classes`, { credentials: 'include' }),
        fetch(`${API}/attendance/${classId}/${dateStr}?_=1${sessionParam}`, { credentials: 'include' })
      ]);
      
      if (studentsRes.ok) setStudents(await studentsRes.json());
      if (classesRes.ok) {
        const classes = await classesRes.json();
        setClassInfo(classes.find(c => c.class_id === classId));
      }
      if (attendanceRes.ok) {
        const records = await attendanceRes.json();
        const attendanceMap = {};
        const lateTimesMap = {};
        records.forEach(r => {
          attendanceMap[r.student_id] = r.status;
          if (r.late_time) lateTimesMap[r.student_id] = r.late_time;
        });
        setAttendance(attendanceMap);
        setLateTimes(lateTimesMap);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [classId, dateStr]);

  const handleStatusChange = (studentId, status) => {
    setAttendance(prev => {
      const newStatus = prev[studentId] === status ? null : status;
      if (newStatus !== 'late') {
        setLateTimes(lt => { const copy = { ...lt }; delete copy[studentId]; return copy; });
      }
      return { ...prev, [studentId]: newStatus };
    });
  };

  const handleLateTimeChange = (studentId, time) => {
    setLateTimes(prev => ({ ...prev, [studentId]: time }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const records = Object.entries(attendance)
        .filter(([_, status]) => status)
        .map(([student_id, status]) => ({
          class_id: classId,
          student_id,
          date: dateStr,
          status,
          late_time: status === 'late' ? (lateTimes[student_id] || null) : null,
          period: session?.class_id === classId ? session.period : null,
          session_id: session?.session_id || null
        }));

      if (records.length === 0) {
        toast.error('No attendance to save');
        setSaving(false);
        return;
      }

      const response = await fetch(`${API}/attendance/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(records)
      });

      if (response.ok) {
        toast.success('Attendance saved successfully');
        refreshStats();
      } else {
        toast.error('Failed to save attendance');
      }
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const markAllPresent = () => {
    const newAttendance = {};
    students.forEach(s => {
      newAttendance[s.student_id] = 'present';
    });
    setAttendance(newAttendance);
    setLateTimes({});
  };

  const stats = {
    present: Object.values(attendance).filter(s => s === 'present').length,
    absent: Object.values(attendance).filter(s => s === 'absent').length,
    late: Object.values(attendance).filter(s => s === 'late').length,
    unmarked: students.length - Object.values(attendance).filter(s => s).length
  };

  const formatLateTime = (time) => {
    if (!time) return '';
    const [hours, mins] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${mins} ${ampm}`;
  };

  const generateExportText = () => {
    const presentStudents = students.filter(s => attendance[s.student_id] === 'present');
    const lateStudents = students.filter(s => attendance[s.student_id] === 'late');
    const absentStudents = students.filter(s => attendance[s.student_id] === 'absent');
    const unmarkedStudents = students.filter(s => !attendance[s.student_id]);

    let text = `Attendance Roll - ${classInfo?.name || 'Class'}\n`;
    text += `Date: ${format(selectedDate, 'EEEE, MMMM d, yyyy')}\n`;
    text += `---\n\n`;

    if (presentStudents.length > 0) {
      text += `PRESENT (${presentStudents.length}):\n`;
      presentStudents.forEach(s => { text += `- ${s.name}\n`; });
      text += `\n`;
    }

    if (lateStudents.length > 0) {
      text += `LATE (${lateStudents.length}):\n`;
      lateStudents.forEach(s => {
        const lt = lateTimes[s.student_id];
        text += `- ${s.name}${lt ? ` (arrived: ${formatLateTime(lt)})` : ''}\n`;
      });
      text += `\n`;
    }

    if (absentStudents.length > 0) {
      text += `ABSENT (${absentStudents.length}):\n`;
      absentStudents.forEach(s => { text += `- ${s.name}\n`; });
      text += `\n`;
    }

    if (unmarkedStudents.length > 0) {
      text += `UNMARKED (${unmarkedStudents.length}):\n`;
      unmarkedStudents.forEach(s => { text += `- ${s.name}\n`; });
      text += `\n`;
    }

    text += `---\n`;
    text += `Total: ${students.length} | Present: ${stats.present} | Late: ${stats.late} | Absent: ${stats.absent}`;
    if (stats.unmarked > 0) text += ` | Unmarked: ${stats.unmarked}`;

    return text;
  };

  const handleCopyExport = () => {
    navigator.clipboard.writeText(generateExportText());
    toast.success('Copied to clipboard - ready to paste in email');
  };

  const breadcrumb = [
    { label: 'Teach Now', path: '/app/live-mode' },
    { label: classInfo?.name || 'Class', path: '/app/live-mode' },
    { label: 'Attendance' }
  ];

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="space-y-6" data-testid="attendance-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-medium text-google-grey-900">Attendance</h1>
            <p className="text-google-grey-600 mt-1">{classInfo?.name} &middot; {format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="border-google-grey-300 text-google-grey-700 hover:bg-google-grey-100" data-testid="date-picker-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {format(selectedDate, 'MMM d, yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              className="border-google-grey-300 text-google-grey-700 hover:bg-google-grey-100"
              onClick={() => setExportOpen(true)}
              data-testid="export-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button 
              onClick={handleSaveAll} 
              disabled={saving}
              className="bg-google-blue hover:bg-blue-600 text-white shadow-google hover:shadow-google-hover transition-shadow"
              data-testid="save-attendance-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save All'}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-white border border-google-grey-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-google-green">{stats.present}</p>
              <p className="text-sm text-google-grey-600">Present</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-google-grey-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-google-red">{stats.absent}</p>
              <p className="text-sm text-google-grey-600">Absent</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-google-grey-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-google-yellow">{stats.late}</p>
              <p className="text-sm text-google-grey-600">Late</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-google-grey-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-google-grey-500">{stats.unmarked}</p>
              <p className="text-sm text-google-grey-600">Unmarked</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick actions */}
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={markAllPresent} 
            className="border-google-grey-300 text-google-green hover:bg-green-50"
            data-testid="mark-all-present-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Mark All Present
          </Button>
        </div>

        {/* Student list */}
        {loading ? (
          <div className="text-center py-12 text-google-grey-500">Loading...</div>
        ) : students.length === 0 ? (
          <Card className="bg-white border border-dashed border-google-grey-300 shadow-none">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-google-grey-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-google-grey-800 mb-2">No students in this class</h3>
              <p className="text-google-grey-500">Add students to start tracking attendance</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border border-google-grey-200">
            <CardContent className="p-0">
              <div className="divide-y divide-google-grey-200">
                {students.map((student) => {
                  const status = attendance[student.student_id];
                  return (
                    <div 
                      key={student.student_id} 
                      className="p-4 hover:bg-google-grey-50 transition-colors"
                      data-testid={`attendance-row-${student.student_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <StudentAvatar student={student} size="md" />
                          <span className="font-medium text-google-grey-900">{student.name}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant={status === 'present' ? 'default' : 'outline'}
                            size="sm"
                            className={status === 'present' 
                              ? 'bg-google-green hover:bg-green-600 text-white' 
                              : 'border-google-grey-300 text-google-green hover:bg-green-50'
                            }
                            onClick={() => handleStatusChange(student.student_id, 'present')}
                            data-testid={`present-btn-${student.student_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Present
                          </Button>
                          <Button
                            variant={status === 'late' ? 'default' : 'outline'}
                            size="sm"
                            className={status === 'late' 
                              ? 'bg-google-yellow hover:bg-yellow-500 text-white' 
                              : 'border-google-grey-300 text-google-yellow hover:bg-yellow-50'
                            }
                            onClick={() => handleStatusChange(student.student_id, 'late')}
                            data-testid={`late-btn-${student.student_id}`}
                          >
                            <Clock className="w-4 h-4 mr-1" />
                            Late
                          </Button>
                          <Button
                            variant={status === 'absent' ? 'default' : 'outline'}
                            size="sm"
                            className={status === 'absent' 
                              ? 'bg-google-red hover:bg-red-600 text-white' 
                              : 'border-google-grey-300 text-google-red hover:bg-red-50'
                            }
                            onClick={() => handleStatusChange(student.student_id, 'absent')}
                            data-testid={`absent-btn-${student.student_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Absent
                          </Button>
                        </div>
                      </div>
                      {/* Late time input */}
                      {status === 'late' && (
                        <div className="mt-3 ml-13 flex items-center gap-3 animate-fade-in pl-[52px]">
                          <label className="text-sm text-google-grey-600 whitespace-nowrap">Arrived at:</label>
                          <Input
                            type="time"
                            value={lateTimes[student.student_id] || ''}
                            onChange={(e) => handleLateTimeChange(student.student_id, e.target.value)}
                            className="w-36 h-9 border-google-grey-300 text-sm"
                            data-testid={`late-time-${student.student_id}`}
                          />
                          {lateTimes[student.student_id] && (
                            <span className="text-sm text-google-grey-500">
                              ({formatLateTime(lateTimes[student.student_id])})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export Dialog */}
        <Dialog open={exportOpen} onOpenChange={setExportOpen}>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-google-grey-900">Export Attendance</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-google-grey-600">
                Copy this text and paste it into an email to send to admin.
              </p>
              <div className="bg-google-grey-50 border border-google-grey-200 rounded-lg p-4 max-h-80 overflow-y-auto">
                <pre className="text-sm text-google-grey-800 whitespace-pre-wrap font-mono" data-testid="export-text">
                  {generateExportText()}
                </pre>
              </div>
              <Button
                onClick={handleCopyExport}
                className="w-full bg-google-blue hover:bg-blue-600 text-white"
                data-testid="copy-export-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy to Clipboard
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
